import { z } from 'zod';
import { refersTo } from '../common/refers-to';

/** At most this many 360° tours per Objekt (IMM-1164). Tours do not count toward `MEDIA_GALLERY_CAP`. */
export const PROPERTY_TOUR_LIMIT = 3;

export const PROPERTY_TOUR_LINK_MAX_LENGTH = 2_000;

export const PROPERTY_TOUR_TITLE_MAX_LENGTH = 80;

/**
 * Tour providers recognised by the link's host (IMM-1164 Detailed Business
 * Rule 2). Display names live in i18n. A host matching none has no provider.
 */
export const TOUR_PROVIDERS = [
  'MATTERPORT',
  'OGULO',
  'IMMOVIEWER',
  'THREE_D_VISTA',
  'GIRAFFE360',
  'KUULA',
  'PANOEE',
  'NODALVIEW',
] as const;

export const TourProviderSchema = z.enum(TOUR_PROVIDERS);

export type TourProvider = z.infer<typeof TourProviderSchema>;

export const PropertyTourSchema = z.object({
  id: z.uuid(),
  // The stored, normalised https URL (`new URL(link).href`). Opened at the provider, never embedded.
  link: z.string(),
  title: z.string(),
  // Derived from `link` on every read; null for an unknown host.
  provider: TourProviderSchema.nullable(),
  // 0-based order within the Objekt's tours.
  position: z.number().int().nonnegative(),
  // IMM-794 marketing set: may be shown on the web exposé, never sent to a portal.
  isPublished: z.boolean(),
  createdByMemberId: refersTo('member', z.uuid()),
  // May the caller delete it: they added it, or they hold `media-manage-any`.
  canManage: z.boolean(),
  createdAt: z.iso.datetime(),
});

export type PropertyTour = z.infer<typeof PropertyTourSchema>;

export const ListPropertyToursResponseSchema = z.object({
  // Ordered by `position`.
  tours: z.array(PropertyTourSchema),
});

export type ListPropertyToursResponse = z.infer<typeof ListPropertyToursResponseSchema>;

export const PropertyToursPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const PropertyTourPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  tourId: z.uuid(),
});

// `link` is checked by the domain, not here: a Zod failure is a generic 400 the
// app may not render (ADR 0014), and the refusal must carry its own code.
export const SavePropertyTourBodySchema = z
  .object({
    link: z.string(),
    title: z.string().trim().min(1).max(PROPERTY_TOUR_TITLE_MAX_LENGTH),
  })
  .strict();

export type SavePropertyTourBody = z.infer<typeof SavePropertyTourBodySchema>;

export const ReorderPropertyToursBodySchema = z
  .object({
    orderedIds: z.array(z.uuid()).min(1),
  })
  .strict();

export type ReorderPropertyToursBody = z.infer<typeof ReorderPropertyToursBodySchema>;

export const SetPropertyTourPublishedBodySchema = z
  .object({
    isPublished: z.boolean(),
  })
  .strict();

export type SetPropertyTourPublishedBody = z.infer<typeof SetPropertyTourPublishedBodySchema>;
